// The easing an animation the assistant adds starts with when the plan gives none (G11 PR 2d):
// live's assistant copies each preset's default options from its catalog (core's
// ANIMATION_PRESETS and GSAP_PRESETS, read for behaviour), where the popover starts every preset
// Linear. An In eases out and an Out the same curve in; live's GSAP eases are taken as near as the
// editor's easings allow (power2 and stronger as cubic, back, bounce and elastic as back, sine as
// quad, none as linear).
import type { AnimationEasing, AnimationMode, AnimationPreset } from "../../../api/generated/model";

/** Each preset's In easing, live's default. */
const IN_EASING: Readonly<Record<AnimationPreset, AnimationEasing>> = {
  fade: "ease-out-quad",
  zoom: "ease-out-back",
  slide: "ease-out-cubic",
  blur: "ease-out-quad",
  wipe: "ease-out-quad",
  "circle-reveal": "ease-out-quad",
  "rect-expand": "ease-out-quad",
  "angle-wipe": "ease-out-quad",
  "center-expand": "ease-out-quad",
  "edge-crop": "ease-out-quad",
  pulse: "ease-out-quad",
  "appear-by-word": "linear",
  "fade-by-word": "linear",
  "pop-caption": "ease-out-quad",
  "bounce-caption": "ease-out-quad",
  "scale-caption": "ease-out-quad",
  "slide-caption": "ease-out-quad",
  "slide-by-word": "ease-out-cubic",
  "slide-mask-word": "ease-out-cubic",
  // Live's engine eases Block Reveal in and out when it is given no easing.
  "block-reveal": "ease-in-out-cubic",
  "char-fade": "ease-out-back",
  "char-slide-up": "ease-out-cubic",
  "char-typewriter": "linear",
  "char-fade-in": "ease-out-cubic",
  "char-scramble": "ease-out-cubic",
  "pop-by-word": "linear",
  "scale-fade-by-word": "ease-out-back",
  "bounce-by-word": "ease-out-back",
  "rotate-by-word": "ease-out-cubic",
  "fade-rotate-by-word": "ease-out-cubic",
  "skew-by-word": "ease-out-cubic",
  "wave-by-word": "ease-in-out-quad",
  "blur-by-word": "ease-out-cubic",
  "drop-soft-by-word": "ease-out-cubic",
  "elastic-pop-by-word": "ease-out-back",
  "flip-up-by-word": "ease-out-cubic",
  "spin-by-word": "ease-out-cubic",
  "stretch-by-word": "ease-out-cubic",
  "reveal-zoom-by-word": "ease-out-cubic",
  "float-wave-by-word": "ease-out-quad",
  "combo-zoom-1": "linear",
  "combo-zoom-2": "linear",
  "combo-pendulum-1": "linear",
  "combo-pendulum-2": "linear",
  "combo-right-distort": "linear",
  "combo-left-distort": "linear",
  "combo-wobble": "linear",
  "combo-spinning-top-1": "linear",
  "combo-spinning-top-2": "linear",
  "combo-sway-out": "linear",
  "combo-bounce-1": "linear",
  "combo-sway-in": "linear",
  custom: "linear",
};

/** An Out's curve: its In's, eased in where the In eases out. */
const OUT_OF: Readonly<Partial<Record<AnimationEasing, AnimationEasing>>> = {
  "ease-out-quad": "ease-in-quad",
  "ease-out-cubic": "ease-in-cubic",
  "ease-out-back": "ease-in-back",
};

/** The easing a preset starts with in `mode`, as live's assistant catalog has it. */
export function presetEasing(preset: AnimationPreset, mode: AnimationMode | null): AnimationEasing {
  const easing = IN_EASING[preset];
  // Live's Pulse is one preset, the same either way.
  return mode === "out" && preset !== "pulse" ? (OUT_OF[easing] ?? easing) : easing;
}

/** The cycles a preset starts with: live's Pulse beats three times, every other once. */
export function presetIterations(preset: AnimationPreset): number {
  return preset === "pulse" ? 3 : 1;
}
